import { json } from "@codemirror/lang-json";
import { python } from "@codemirror/lang-python";
import { KeyBinding, keymap } from "@codemirror/view";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Save from "@mui/icons-material/Save";
import {
  IconButton,
  Paper,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material";
import { CasLoader } from "./brand/CasLoader";
import { Box } from "@mui/system";
import ReactCodeMirror, { Prec } from "@uiw/react-codemirror";
import { CSSProperties, useEffect, useState } from "react";
import { useIsDarkMode } from "../hook/setting";

export enum CodeLang {
  JSON = "JSON",
  PYTHON = "PYTHON",
}

export interface CodeEditorProps {
  code: string;
  writeCode: (code: string) => Promise<any>;
  logs: string;
  codeLang?: CodeLang;
  runCode?: (code: string) => Promise<any>;
}

export const CodeEditorCode: React.FC<CodeEditorProps> = ({
  code: codeState,
  writeCode,
  codeLang,
  runCode: runCodeProp,
}) => {
  const [codeLocal, _setCodeLocal] = useState("");
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const editControlsDisabled = !unsavedChanges || isSaving;
  const hideCircularProgessSxProps: SxProps = isSaving
    ? {}
    : { display: "none" };
  const {isDarkMode} = useIsDarkMode();
  const langExtension =
    codeLang === CodeLang.PYTHON
      ? python()
      : codeLang === CodeLang.JSON
      ? json()
      : python();
  const extensions = [langExtension];

  useEffect(() => {
    setCodeLocal(codeState);
  }, [codeState]);

  const setCodeLocal = (code: string) => {
    const cleanedCode = code.replace(/^```python\s*|\s*```$/g, "");
    _setCodeLocal(cleanedCode);
  };

  const onSave = () => {
    if (isSaving) return;

    setIsSaving(true);
    writeCode(codeState).finally(() => {
      setIsSaving(false);
      setUnsavedChanges(false);
    });
  };

  const runCode = (code: string) => {
    if (!runCodeProp) {
      throw new Error("Cannot run code the parm isn't defined.");
    }
    setIsSaving(true);
    return runCodeProp(code).finally(() => {
      setIsSaving(false);
      setUnsavedChanges(false);
    });
  };

  const runCodeKeyMap: KeyBinding[] = runCodeProp
    ? [
        {
          key: "Ctrl-Enter",
          preventDefault: true,
          run: () => {
            runCode(codeLocal);
            return true;
          },
        },
      ]
    : [];

  const codeKeyMap = Prec.highest(
    keymap.of([
      {
        key: "Ctrl-s",
        preventDefault: true,
        run: () => {
          onSave();
          return true;
        },
      },
      ...runCodeKeyMap,
    ])
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          height: "3rem",
        }}
      >
        <Typography variant="subtitle1">Code</Typography>
        <Box
          sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        >
          <CasLoader
            sx={hideCircularProgessSxProps}
            size={24}
          />
          <IconButton
            color="primary"
            onClick={onSave}
            disabled={editControlsDisabled}
          >
            <Save />
          </IconButton>
          {runCodeProp && (
            <IconButton color="primary" onClick={() => runCode(codeLocal)}>
              <PlayArrow />
            </IconButton>
          )}
        </Box>
      </Box>
      <ReactCodeMirror
        editable={true}
        value={codeLocal}
        theme={isDarkMode ? "dark" : "light"}
        height="40rem"
        extensions={[codeKeyMap, ...extensions]}
        onChange={(value: any) => {
          setUnsavedChanges(true);
          setCodeLocal(value);
        }}
      />
    </>
  );
};

export const CodeEditorLogs: React.FC<CodeEditorProps> = ({ logs }) => {
  const [logsLoading, setLogsLoading] = useState(false);
  const theme = useTheme();
  const {isDarkMode} = useIsDarkMode();
  const colorProps: CSSProperties = isDarkMode
    ? { backgroundColor: "rgb(29, 31, 33)", color: "rgb(197, 200, 198)" }
    : { backgroundColor: theme.palette.grey.A200 };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "start",
          height: "3rem",
        }}
      >
        <Typography variant="subtitle1">Logs</Typography>
      </Box>
      {logsLoading ? (
        <Box
          sx={{ padding: "1rem", display: "flex", justifyContent: "center" }}
        >
          <CasLoader />
        </Box>
      ) : (
        <div
          style={{
            width: "100%",
            height: "40rem",
            padding: "1rem",
            overflowY: "auto",
            whiteSpace: "pre",
            ...colorProps,
          }}
        >
          {logs ? logs : "No Logs"}
        </div>
      )}
    </>
  );
};

export const CodeEditor: React.FC<CodeEditorProps> = (props) => {
  const paperStyles = {
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    height: "50rem",
    width: "100%",
    padding: 1,
  };
  return (
    <Box
      sx={{
        flexDirection: "row",
        display: "flex",
      }}
    >
      <Paper elevation={7} sx={{ ...paperStyles, width: "66%" }}>
        <CodeEditorCode {...props} />
      </Paper>
      <Paper elevation={7} sx={{ ...paperStyles, width: "33%" }}>
        <CodeEditorLogs {...props} />
      </Paper>
    </Box>
  );
};
